"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { HistoryRange } from "@/lib/app-detail/daily-buckets";
import type { RatingsHistoryResult } from "@/lib/app-detail/ratings-history";

const EMPTY: RatingsHistoryResult = { items: [], byCountry: [], meta: { range: "all", country: "us", bound: 1000 } };

/** Average rating and rating count over time for one storefront, plus the newest snapshot per storefront. */
export function useAppRatingsHistory(appId: number, country: string, range: HistoryRange, enabled = true) {
  const { data, isPending, isFetching, error } = useQuery<RatingsHistoryResult, Error>({
    queryKey: ["app-ratings-history", appId, country, range],
    enabled: !!appId && enabled,
    placeholderData: keepPreviousData,
    // The collector snapshots ratings a few times a day at most.
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/app-detail/ratings-history?appId=${appId}&country=${encodeURIComponent(country)}&range=${range}`);
      if (!res.ok) throw new Error(`Failed to fetch rating history: ${res.statusText}`);
      return (await res.json()) as RatingsHistoryResult;
    },
  });
  // isFetching covers a range switch: keepPreviousData keeps the old items in `data` until the new range lands.
  return { data: data ?? EMPTY, isLoading: isPending, isFetching, error: error?.message ?? null };
}
